// Üst kimlik şeridi: her bölümde görünen ad, kodlar, birim, kategori ve görsel.
import React from 'react';
import {
  Autocomplete, Box, Button, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select,
  Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';

export const IdentitySection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const cover = f.imageUrls[0];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '88px minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Tooltip title={t('stock.addModal.layout.imageTooltip', 'Görsel ekle veya değiştir')}>
        <Box
          component="button"
          type="button"
          onClick={() => f.setIsImageLibraryModalOpen(true)}
          aria-label={t('stock.addModal.layout.imageTooltip', 'Görsel ekle veya değiştir')}
          sx={{
            display: { xs: 'none', md: 'grid' },
            placeItems: 'center',
            width: 88,
            height: 88,
            borderRadius: 2,
            border: '1.5px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            cursor: 'pointer',
            overflow: 'hidden',
            p: 0,
            color: 'text.secondary',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          {cover ? (
            <Box component="img" src={cover} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ textAlign: 'center', px: 0.5 }}>
              <AddPhotoAlternateOutlinedIcon />
              <Typography variant="caption" display="block">{t('stock.addModal.layout.image', 'Görsel')}</Typography>
            </Box>
          )}
        </Box>
      </Tooltip>

      <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField
            id="stock-card-name"
            fullWidth
            required
            size="small"
            label={f.itemType === 'SERVICE'
              ? t('stock.addModal.layout.serviceName', 'Hizmet adı')
              : t('stock.addModal.fields.productName')}
            value={f.itemName}
            onChange={(e) => f.setItemName(e.target.value)}
            error={!f.itemName.trim()}
            InputProps={{ sx: { fontSize: '1.05rem', fontWeight: 600 } }}
          />
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch checked={f.isActive} onChange={(e) => f.setIsActive(e.target.checked)} />}
            label={f.isActive ? t('stock.addModal.layout.active', 'Aktif') : t('stock.addModal.layout.passive', 'Pasif')}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          <TextField
            id="stock-card-code"
            fullWidth
            size="small"
            label={t('stock.addModal.fields.stockCode')}
            value={f.stockCode}
            onChange={(e) => f.setStockCode(e.target.value)}
            placeholder={t('stock.addModal.fields.stockCodeAuto')}
            helperText={f.isEditMode ? undefined : t('stock.addModal.layout.autoIfEmpty', 'Boşsa otomatik verilir')}
          />
          <TextField
            id="stock-card-barcode"
            fullWidth
            size="small"
            label={t('stock.addModal.layout.barcode', 'Barkod')}
            value={f.barcode}
            onChange={(e) => {
              f.setBarcode(e.target.value);
              if (f.barcodeExists) {
                f.setBarcodeExists(false);
                f.setExistingItem(null);
                f.setError(null);
              }
            }}
            error={f.barcodeExists}
            helperText={f.barcodeExists
              ? t('stock.addModal.layout.barcodeTaken', 'Bu barkod başka bir üründe kayıtlı')
              : (f.isEditMode ? undefined : t('stock.addModal.layout.autoIfEmpty', 'Boşsa otomatik verilir'))}
          />
          <FormControl fullWidth size="small" required>
            <InputLabel id="stock-card-unit-label">{t('stock.addModal.layout.unit', 'Birim')}</InputLabel>
            <Select
              labelId="stock-card-unit-label"
              id="stock-card-unit"
              label={t('stock.addModal.layout.unit', 'Birim')}
              value={f.unit}
              onChange={(e) => f.setUnit(String(e.target.value))}
              disabled={f.baseUnitLocked}
            >
              {f.unitOptions.map(option => (
                <MenuItem key={option} value={option}>{f.getUnitLabel(option)}</MenuItem>
              ))}
              {!f.unitOptions.includes(f.unit as typeof f.unitOptions[number]) && f.unit && (
                <MenuItem value={f.unit}>{f.unit}</MenuItem>
              )}
            </Select>
            {f.baseUnitLocked && (
              <FormHelperText sx={{ color: 'warning.main' }}>{t('units.baseUnitLocked', { ns: 'production' })}</FormHelperText>
            )}
          </FormControl>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
            <Autocomplete
              fullWidth
              size="small"
              id="stock-card-category"
              options={f.getFilteredCategoryOptions()}
              getOptionLabel={(option) => option.name}
              value={f.selectedCategory}
              onChange={(_, value) => f.handleCategoryChange(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label={t('stock.addModal.fields.category')} size="small" />}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ pl: (option.level - 1) * 2, display: 'flex', alignItems: 'center', fontWeight: option.isParent ? 700 : 400 }}>
                    <CategoryIcon fontSize="small" sx={{ mr: 1, color: option.isParent ? 'primary.main' : 'action.active' }} />
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>{option.label}</Typography>
                  </Box>
                </li>
              )}
            />
            <Tooltip title={t('stock.addModal.layout.newCategory', 'Yeni kategori')}>
              <Button
                variant="outlined"
                size="small"
                onClick={f.handleOpenCategoryModal}
                aria-label={t('stock.addModal.layout.newCategory', 'Yeni kategori')}
                sx={{ minWidth: 40, height: 40, p: 0 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
